'use client'

// FirstPersonHud — the DOM layer of first-person interactions:
//   • crosshair dot (brightens + labels when over an offering crystal)
//   • wall-bump impact overlay (anime-style radial lines, one flash per hit)
//   • offering viewer, opened by clicking a crystal (pointer lock releases;
//     clicking the world afterwards re-locks and walking resumes)
// The in-canvas half (aim scan, click, bump detection) lives in
// FirstPersonController; they talk through gameStore (fpAim, openedOffering,
// wallBumpAt).

import { useEffect, useState } from 'react'
import { authFetch } from '../auth/useUser'
import useGameStore from '../../stores/gameStore'
import { tierLook } from '../../lib/world/sacrificeTiers'
import { ui, kawaii, ink } from '../../lib/ui/theme'
import { liftAsk, callLift } from '../../lib/world/liftClient'

const PHASE_LABEL = {
  pending: "he hasn't found this one yet.",
  accepted: 'he kept this one.',
  rejected: 'he let this one go.',
}

export function FirstPersonHud() {
  const droneActive = useGameStore((s) => s.droneActive)
  const opened = useGameStore((s) => s.openedOffering)
  const inFp = !droneActive
  return (
    <>
      {inFp && !opened && <Crosshair />}
      {inFp && !opened && <OfferingPill />}
      {inFp && !opened && <LiftPill />}
      {inFp && <WallBump />}
      {opened && <OfferingViewer offering={opened} />}
    </>
  )
}

// Standing near a placed offering puts a button on screen, the same shape
// as the actions pill: walk up, press, read. This replaced the label that
// used to hang under the crosshair, which needed you to AIM at the
// crystal to learn you could read it, and told you with a line of
// floating text rather than a thing to press. Pending only: kept ones
// live in the ruins as a display, and a doorbell on a shelf of
// twenty-seven crystals would ring the whole visit.
const OFFERING_NEAR = 9
const OFFERING_POLL_MS = 450

function OfferingPill() {
  const [near, setNear] = useState(null)

  useEffect(() => {
    const t = setInterval(() => {
      const g = useGameStore.getState()
      const get = g.playerPosGetter
      const pos = get ? get() : null
      if (!pos) { setNear(null); return }
      let best = null
      let bestD = OFFERING_NEAR * OFFERING_NEAR
      for (const e of g.entityStates || []) {
        if (e.type !== 'SACRIFICE' || e.state?.phase !== 'pending') continue
        const dx = pos.x - e.pos.x
        const dz = pos.z - e.pos.z
        const d = dx * dx + dz * dz
        if (d < bestD) { bestD = d; best = e }
      }
      setNear((cur) => (cur?.id === (best?.id || null) ? cur : best ? { id: best.id, ...best.state } : null))
    }, OFFERING_POLL_MS)
    return () => clearInterval(t)
  }, [])

  if (!near) return null
  const { color } = tierLook(near.tier)
  return (
    <button
      type="button"
      onClick={() => useGameStore.getState().setOpenedOffering(near)}
      className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-auto"
      style={{
        // Sits a step above the actions pill so the two never overlap
        // when an offering lands on a doorstep.
        bottom: 138,
        background: ink.bg,
        border: `2.5px solid ${ink.border}`,
        borderRadius: 999,
        color: ink.text,
        padding: '9px 20px',
        fontSize: 13.5,
        fontWeight: 800,
        boxShadow: '0 3px 0 rgba(0, 0, 0, 0.5)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
      }}
    >
      <span style={{ width: 9, height: 9, borderRadius: 999, background: color, boxShadow: `0 0 6px 1px ${color}` }} />
      {near.handle ? `${near.handle}'s offering` : 'an offering'}
    </button>
  )
}

// By the monorail lift's doors, or inside its car: a button that calls it,
// or sends it on to the other landing. L does the same while walking with
// the pointer locked (FirstPersonController), so the pill says so too.
const LIFT_POLL_MS = 300

function LiftPill() {
  const [ask, setAsk] = useState(null)
  useEffect(() => {
    const t = setInterval(() => {
      const get = useGameStore.getState().playerPosGetter
      const pos = get ? get() : null
      const a = pos ? liftAsk(pos.x, pos.y, pos.z) : null
      setAsk((cur) => (cur?.words === a?.words && cur?.level === a?.level ? cur : a))
    }, LIFT_POLL_MS)
    return () => clearInterval(t)
  }, [])
  if (!ask) return null
  return (
    <button
      type="button"
      onClick={() => callLift(ask)}
      className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-auto"
      style={{
        bottom: 138,
        background: ink.bg,
        border: `2.5px solid ${ink.border}`,
        borderRadius: 999,
        color: ink.text,
        padding: '9px 20px',
        fontSize: 13.5,
        fontWeight: 800,
        boxShadow: '0 3px 0 rgba(0, 0, 0, 0.5)',
        cursor: 'pointer',
      }}
    >
      L: {ask.words}
    </button>
  )
}

function Crosshair() {
  const aim = useGameStore((s) => s.fpAim)
  return (
    <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center">
      <div className="flex flex-col items-center" style={{ transform: 'translateY(-2px)' }}>
        <div
          style={{
            width: aim ? 8 : 5,
            height: aim ? 8 : 5,
            borderRadius: 999,
            background: aim ? '#facc15' : 'rgba(255,255,255,0.75)',
            boxShadow: aim ? '0 0 8px 2px rgba(250,204,21,0.5)' : 'none',
            transition: 'all 120ms ease',
          }}
        />
        {/* The floating "click to read" line lived here; the offering
            pill replaced it (a button beats a caption). The dot still
            brightens as aim feedback for the locked-cursor click. */}
      </div>
    </div>
  )
}

// One flash per wall hit: short radial impact lines + a breath of vignette.
function WallBump() {
  const at = useGameStore((s) => s.wallBumpAt)
  const [show, setShow] = useState(0)
  useEffect(() => {
    if (!at) return
    setShow(at)
    const t = setTimeout(() => setShow(0), 300)
    return () => clearTimeout(t)
  }, [at])
  if (!show) return null

  const lines = []
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + 0.26
    const cx = 50 + Math.cos(a) * 47
    const cy = 50 + Math.sin(a) * 47
    const ix = 50 + Math.cos(a) * (39 - (i % 3) * 2.5)
    const iy = 50 + Math.sin(a) * (39 - (i % 3) * 2.5)
    lines.push(<line key={i} x1={cx} y1={cy} x2={ix} y2={iy} stroke="white" strokeWidth="0.7" strokeLinecap="round" />)
  }
  return (
    <div key={show} className="fixed inset-0 z-40 pointer-events-none" style={{ animation: 'kiwi-bump 300ms ease-out forwards' }}>
      <style>{`@keyframes kiwi-bump { from { opacity: 0.5; } to { opacity: 0; } }`}</style>
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
        {lines}
      </svg>
    </div>
  )
}

function OfferingViewer({ offering }) {
  const setOpenedOffering = useGameStore((s) => s.setOpenedOffering)
  const close = () => setOpenedOffering(null)
  const [copied, setCopied] = useState(false)

  // The crystal's wire state withholds a pending image on purpose: his
  // verdict is the only moderation, so an unjudged picture never reaches
  // other people's screens. The sender is the one person who may see it
  // anyway (they uploaded it), so when the image is missing we ask for our
  // own offerings and match by id. Signed out or someone else's: nothing
  // comes back, and the card says a picture is waiting.
  const [ownImage, setOwnImage] = useState(null)
  useEffect(() => {
    setOwnImage(null)
    if (offering.imageUrl || offering.kind !== 'image' || !offering.offeringId) return
    let dead = false
    // authFetch, not fetch: ?mine=1 verifies the bearer token and answers
    // an empty list without one, so the bare call could never see the
    // sender's own picture and this whole beat was silently dead. Found in
    // the 31 Aug audit; OfferingsDock hits the same query correctly.
    authFetch('/api/sacrifices?mine=1&limit=50')
      .then((r) => r.json())
      .then((d) => {
        if (dead) return
        const mine = (d.sacrifices || []).find((s) => s.id === offering.offeringId)
        if (mine?.imageUrl) setOwnImage(mine.imageUrl)
      })
      .catch(() => {})
    return () => { dead = true }
  }, [offering])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const phase = offering.phase || 'pending'
  const kept = phase === 'accepted'
  const gone = phase === 'rejected'
  const { color } = tierLook(offering.tier)
  const image = offering.imageUrl || ownImage
  const kindLabel = offering.kind === 'financial' ? 'a tribute' : offering.kind === 'image' ? 'a picture' : 'a message'
  const share = () => {
    if (!offering.offeringId) return
    const url = `${window.location.origin}/o/${offering.offeringId}`
    navigator.clipboard?.writeText(url)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      .catch(() => {})
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto"
      style={{ background: 'rgba(20, 15, 40, 0.45)', backdropFilter: 'blur(4px)' }}
      onClick={close}
    >
      <div
        className="w-[min(92vw,390px)]"
        style={{
          background: kawaii.cream,
          border: kawaii.outline,
          borderRadius: kawaii.radius,
          boxShadow: kawaii.drop,
          padding: '15px 17px',
          fontFamily: ui.font,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="flex items-center gap-2 min-w-0">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: color, border: `2px solid ${kawaii.ink}` }}
            />
            <span style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 800 }} className="truncate">
              {offering.handle || 'anon'}
            </span>
            <span style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 700 }} className="shrink-0">
              left {kindLabel}
            </span>
          </span>
          <button
            onClick={close}
            aria-label="close"
            style={{ color: kawaii.inkSoft, fontSize: 15, fontWeight: 800, lineHeight: 1 }}
            className="px-1 shrink-0"
          >
            ✕
          </button>
        </div>

        {image ? (
          <>
            <img
              src={image}
              alt="the offering"
              loading="lazy"
              style={{
                margin: '0 auto 4px', maxHeight: 200, width: 'auto', maxWidth: '100%',
                border: kawaii.outlineThin, borderRadius: 14,
              }}
            />
            {!offering.imageUrl && ownImage && phase === 'pending' && (
              <p style={{ color: kawaii.inkSoft, fontSize: 11.5, fontStyle: 'italic', textAlign: 'center', marginBottom: 8 }}>
                only you can see the picture until he decides
              </p>
            )}
          </>
        ) : offering.kind === 'image' && (
          <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontStyle: 'italic', marginBottom: 8 }}>
            a picture, folded away until he has seen it
          </p>
        )}

        {offering.kind === 'financial' && (offering.asset || offering.amount != null) && (
          <div className="mb-2">
            <span
              style={{
                background: '#a8d6a0', color: kawaii.ink, border: kawaii.outlineThin,
                borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 800,
              }}
            >
              {offering.amount != null ? `${offering.amount} ` : ''}{offering.asset}
              {offering.usdValue != null ? ` · $${Math.round(offering.usdValue)}` : ''}
            </span>
          </div>
        )}

        {offering.text && (
          <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.5, marginBottom: 8 }} className="whitespace-pre-wrap">
            {offering.text}
          </p>
        )}

        {offering.reaction ? (
          <p style={{ color: kept ? '#8a5a12' : '#a83832', fontSize: 13.5, fontStyle: 'italic', lineHeight: 1.5 }}>
            {offering.reaction}
          </p>
        ) : (
          <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontStyle: 'italic' }}>
            {PHASE_LABEL[phase] || PHASE_LABEL.pending}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span
            style={{
              background: kept ? '#a8d6a0' : gone ? '#eda6a0' : kawaii.creamDeep,
              color: kawaii.ink, border: kawaii.outlineThin, borderRadius: 999,
              boxShadow: kawaii.dropSm, padding: '2px 11px', fontSize: 12, fontWeight: 800,
            }}
          >
            {kept ? 'he kept it' : gone ? 'he let it go' : 'waiting'}
          </span>
          {offering.offeringId && (
            <button
              onClick={share}
              style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 800 }}
            >
              {copied ? 'link copied' : 'share'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FirstPersonHud
